/**
 * IndexedDB storage service for offline data persistence
 */

import type { InspectionSchemaDetailResponse } from '../types/inspectionSchema';

export interface CachedSchema {
  siteId: string;
  schema: InspectionSchemaDetailResponse;
  lastFetched: number;
}

export interface SyncQueueItem {
  id: string;
  timestamp: number;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  url: string;
  body?: any;
  headers?: Record<string, string>;
  retryCount: number;
  maxRetries: number;
  status: 'pending' | 'syncing' | 'failed' | 'completed';
  error?: string;
  /** Human label for the sync badge, e.g. the site name. */
  label?: string;
  /**
   * Requests to run after the primary request succeeds. `{inspection_id}` in
   * a url or body is replaced with the id the primary request returned
   * (see pages/lite/liteSubmitReplay.ts).
   */
  followUps?: Array<{
    method: 'POST' | 'PATCH';
    url: string;
    body?: Record<string, unknown>;
  }>;
  /** Id returned by the primary request; set once so retries never repeat it. */
  resultId?: string;
  /** How many follow-ups have completed, so a retry resumes where it stopped. */
  followUpsDone?: number;
}

export interface OfflineInspection {
  id: string;
  localId: string;
  siteId: string;
  data: any;
  photos: Array<{ localId: string; blob: Blob; metadata: any }>;
  created: number;
  lastModified: number;
  syncStatus: 'pending' | 'synced' | 'conflict';
}

// Drafts older than this are considered stale: ignored by resume prompts and
// the dashboard unsubmitted-inspections banner, and cleaned up on next load.
export const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * Set on a draft once its answers have been filed as an inspection: which
 * one, and the parts of the form the answers alone do not carry. The draft is
 * then kept until the report is sent, so Edit on the site screen can reopen
 * the inspection as the inspector left it (Jim, Teams 2026-10-05).
 */
export interface SubmittedSnapshot {
  inspectionId: string;
  conductedDate: string;
  conductedTime: string;
  isFinalInspection: boolean;
  // JIM-1027-11: why the inspector called it final (consultant reads it).
  finalInspectionNote?: string;
  alertConsultant: boolean;
  austinNotes: string;
  austinTaps: unknown[];
  austinConditions?: Record<string, string>;
}

// In-progress inspection state persisted per (site, user) so a mid-inspection
// refresh / crash / app-kill can be resumed without losing answers or CAs.
// After a successful submit it stays, marked `submitted`, until the report is
// sent (see pages/lite/submittedDraft.ts).
export interface InspectionDraft {
  key: string; // `${siteId}:${userId}`
  siteId: string;
  userId: string;
  payload: {
    responses: Record<string, unknown>;
    verifiedCaIds: Record<string, string>;
    caPriorities: Record<string, 'medium' | 'high' | null>;
    // F-Comment universal report comment (optional — older drafts predate it).
    reportComment?: string;
    // #265: this visit also covers a required rain event (sends type "both").
    coversRainEvent?: boolean;
    // JIM-1005-01: begun from the Rain Event box — it is the rain event visit.
    rainEventStart?: boolean;
    // City of Austin section: notes, this visit's taps and the condition
    // lines it changed. All three are filed with the report, so a draft that
    // dropped them lost that work on a reload (kept since 2026-10-05).
    austinNotes?: string;
    austinTaps?: unknown[];
    austinConditions?: Record<string, string>;
    // JIM-1006-05: when the visit happened (opened, arrived, first answer,
    // filed, left) — survives a reload like everything else here.
    visitTiming?: Record<string, unknown>;
    // Present once these answers are filed; absent while still in progress.
    submitted?: SubmittedSnapshot;
  };
  savedAt: number;
}

class OfflineStorage {
  private dbName = 'envirolink-offline';
  private version = 3; // v3 adds drafts store for mid-inspection resume
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  async init(): Promise<void> {
    if (this.initPromise) return this.initPromise;
    this.initPromise = this.openDb().catch((err) => {
      this.initPromise = null;
      throw err;
    });
    return this.initPromise;
  }

  private openDb(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Sync queue for API requests
        if (!db.objectStoreNames.contains('syncQueue')) {
          const syncStore = db.createObjectStore('syncQueue', {
            keyPath: 'id',
          });
          syncStore.createIndex('timestamp', 'timestamp', { unique: false });
          syncStore.createIndex('status', 'status', { unique: false });
        }

        // Offline inspections
        if (!db.objectStoreNames.contains('inspections')) {
          const inspectionStore = db.createObjectStore('inspections', {
            keyPath: 'localId',
          });
          inspectionStore.createIndex('siteId', 'siteId', { unique: false });
          inspectionStore.createIndex('syncStatus', 'syncStatus', {
            unique: false,
          });
          inspectionStore.createIndex('lastModified', 'lastModified', {
            unique: false,
          });
        }

        // Cached sites data
        if (!db.objectStoreNames.contains('sites')) {
          const sitesStore = db.createObjectStore('sites', { keyPath: 'id' });
          sitesStore.createIndex('lastSync', 'lastSync', { unique: false });
        }

        // Photo blobs
        if (!db.objectStoreNames.contains('photos')) {
          const photosStore = db.createObjectStore('photos', {
            keyPath: 'localId',
          });
          photosStore.createIndex('inspectionId', 'inspectionId', {
            unique: false,
          });
        }

        // Cached inspection schemas (for offline access)
        if (!db.objectStoreNames.contains('schemas')) {
          const schemasStore = db.createObjectStore('schemas', {
            keyPath: 'siteId',
          });
          schemasStore.createIndex('lastFetched', 'lastFetched', {
            unique: false,
          });
        }

        // Mid-inspection drafts (resume after refresh / app-kill)
        if (!db.objectStoreNames.contains('drafts')) {
          const draftsStore = db.createObjectStore('drafts', {
            keyPath: 'key',
          });
          draftsStore.createIndex('siteId', 'siteId', { unique: false });
          draftsStore.createIndex('savedAt', 'savedAt', { unique: false });
        }
      };
    });
  }

  /**
   * Every operation waits for the one shared open. main.tsx kicks off init()
   * without awaiting it before React mounts, so on a cold load (first open
   * runs the v3 upgrade) LiteLayout's sync-stats read raced it and rejected
   * with "Database not initialized" — visible as a console error on every
   * fresh install. A failed open still rejects with the real IndexedDB error.
   */
  private async ready(): Promise<IDBDatabase> {
    if (!this.db) {
      await this.init();
    }
    if (!this.db) {
      throw new Error('Database not initialized. Call init() first.');
    }
    return this.db;
  }

  // Sync Queue Operations
  async addToSyncQueue(
    item: Omit<SyncQueueItem, 'id' | 'retryCount' | 'status'>
  ): Promise<string> {
    await this.ready();

    const id = `sync_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const queueItem: SyncQueueItem = {
      ...item,
      id,
      retryCount: 0,
      status: 'pending',
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['syncQueue'], 'readwrite');
      const store = transaction.objectStore('syncQueue');
      const request = store.add(queueItem);

      request.onsuccess = () => resolve(id);
      request.onerror = () => reject(request.error);
    });
  }

  async getSyncQueue(
    status?: 'pending' | 'syncing' | 'failed'
  ): Promise<SyncQueueItem[]> {
    await this.ready();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['syncQueue'], 'readonly');
      const store = transaction.objectStore('syncQueue');

      const request = status
        ? store.index('status').getAll(status)
        : store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async updateSyncQueueItem(
    id: string,
    updates: Partial<SyncQueueItem>
  ): Promise<void> {
    await this.ready();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['syncQueue'], 'readwrite');
      const store = transaction.objectStore('syncQueue');

      const getRequest = store.get(id);
      getRequest.onsuccess = () => {
        const item = getRequest.result;
        if (!item) {
          reject(new Error('Sync queue item not found'));
          return;
        }

        const updated = { ...item, ...updates };
        const putRequest = store.put(updated);
        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () => reject(putRequest.error);
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  async removeSyncQueueItem(id: string): Promise<void> {
    await this.ready();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['syncQueue'], 'readwrite');
      const store = transaction.objectStore('syncQueue');
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Inspection Operations
  async saveInspection(
    inspection: Omit<OfflineInspection, 'localId'>
  ): Promise<string> {
    await this.ready();

    const localId = `inspection_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const offlineInspection: OfflineInspection = {
      ...inspection,
      localId,
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['inspections'], 'readwrite');
      const store = transaction.objectStore('inspections');
      const request = store.add(offlineInspection);

      request.onsuccess = () => resolve(localId);
      request.onerror = () => reject(request.error);
    });
  }

  async getInspection(localId: string): Promise<OfflineInspection | null> {
    await this.ready();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['inspections'], 'readonly');
      const store = transaction.objectStore('inspections');
      const request = store.get(localId);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async getInspectionsBySite(siteId: string): Promise<OfflineInspection[]> {
    await this.ready();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['inspections'], 'readonly');
      const store = transaction.objectStore('inspections');
      const index = store.index('siteId');
      const request = index.getAll(siteId);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async updateInspection(
    localId: string,
    updates: Partial<OfflineInspection>
  ): Promise<void> {
    await this.ready();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['inspections'], 'readwrite');
      const store = transaction.objectStore('inspections');

      const getRequest = store.get(localId);
      getRequest.onsuccess = () => {
        const inspection = getRequest.result;
        if (!inspection) {
          reject(new Error('Inspection not found'));
          return;
        }

        const updated = {
          ...inspection,
          ...updates,
          lastModified: Date.now(),
        };
        const putRequest = store.put(updated);
        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () => reject(putRequest.error);
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  async deleteInspection(localId: string): Promise<void> {
    await this.ready();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        ['inspections', 'photos'],
        'readwrite'
      );

      // Delete inspection
      const inspectionStore = transaction.objectStore('inspections');
      inspectionStore.delete(localId);

      // Delete associated photos
      const photoStore = transaction.objectStore('photos');
      const photoIndex = photoStore.index('inspectionId');
      const photoRequest = photoIndex.openCursor(IDBKeyRange.only(localId));

      photoRequest.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  // Photo Operations
  async savePhoto(
    inspectionId: string,
    blob: Blob,
    metadata: any
  ): Promise<string> {
    await this.ready();

    const localId = `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const photo = {
      localId,
      inspectionId,
      blob,
      metadata,
      timestamp: Date.now(),
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['photos'], 'readwrite');
      const store = transaction.objectStore('photos');
      const request = store.add(photo);

      request.onsuccess = () => resolve(localId);
      request.onerror = () => reject(request.error);
    });
  }

  async getPhoto(
    localId: string
  ): Promise<{ blob: Blob; metadata: any } | null> {
    await this.ready();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['photos'], 'readonly');
      const store = transaction.objectStore('photos');
      const request = store.get(localId);

      request.onsuccess = () => {
        const result = request.result;
        resolve(
          result ? { blob: result.blob, metadata: result.metadata } : null
        );
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Site Cache Operations
  async cacheSites(sites: any[]): Promise<void> {
    await this.ready();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['sites'], 'readwrite');
      const store = transaction.objectStore('sites');

      sites.forEach((site) => {
        store.put({ ...site, lastSync: Date.now() });
      });

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async getCachedSites(): Promise<any[]> {
    await this.ready();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['sites'], 'readonly');
      const store = transaction.objectStore('sites');
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Schema Cache Operations
  async cacheSchemaForSite(
    siteId: string,
    schema: InspectionSchemaDetailResponse
  ): Promise<void> {
    await this.ready();

    const cachedSchema: CachedSchema = {
      siteId,
      schema,
      lastFetched: Date.now(),
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['schemas'], 'readwrite');
      const store = transaction.objectStore('schemas');
      const request = store.put(cachedSchema);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getCachedSchema(siteId: string): Promise<CachedSchema | null> {
    await this.ready();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['schemas'], 'readonly');
      const store = transaction.objectStore('schemas');
      const request = store.get(siteId);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async clearStaleSchemas(
    maxAgeMs: number = 7 * 24 * 60 * 60 * 1000
  ): Promise<void> {
    await this.ready();

    const cutoffTime = Date.now() - maxAgeMs;

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['schemas'], 'readwrite');
      const store = transaction.objectStore('schemas');
      const index = store.index('lastFetched');
      const request = index.openCursor(IDBKeyRange.upperBound(cutoffTime));

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  // Draft Operations
  private draftKey(siteId: string, userId: string): string {
    return `${siteId}:${userId}`;
  }

  async saveDraft(
    siteId: string,
    userId: string,
    payload: InspectionDraft['payload']
  ): Promise<void> {
    await this.ready();

    const draft: InspectionDraft = {
      key: this.draftKey(siteId, userId),
      siteId,
      userId,
      payload,
      savedAt: Date.now(),
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['drafts'], 'readwrite');
      const store = transaction.objectStore('drafts');
      const request = store.put(draft);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getDraft(
    siteId: string,
    userId: string
  ): Promise<InspectionDraft | null> {
    await this.ready();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['drafts'], 'readonly');
      const store = transaction.objectStore('drafts');
      const request = store.get(this.draftKey(siteId, userId));
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  // All of a user's in-progress drafts (dashboard "unsubmitted inspections"
  // banner). Drafts are purely local, so this is the source of truth for
  // inspections answered on this device but never submitted.
  async getDraftsForUser(userId: string): Promise<InspectionDraft[]> {
    await this.ready();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['drafts'], 'readonly');
      const store = transaction.objectStore('drafts');
      const request = store.getAll();
      request.onsuccess = () =>
        resolve(
          ((request.result || []) as InspectionDraft[]).filter(
            (d) => d.userId === userId
          )
        );
      request.onerror = () => reject(request.error);
    });
  }

  async clearDraft(siteId: string, userId: string): Promise<void> {
    await this.ready();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['drafts'], 'readwrite');
      const store = transaction.objectStore('drafts');
      const request = store.delete(this.draftKey(siteId, userId));
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Cleanup Operations
  async clearOldData(daysToKeep = 30): Promise<void> {
    await this.ready();

    const cutoffTime = Date.now() - daysToKeep * 24 * 60 * 60 * 1000;

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(
        ['syncQueue', 'inspections'],
        'readwrite'
      );

      // Clear old sync queue items
      const syncStore = transaction.objectStore('syncQueue');
      const syncIndex = syncStore.index('timestamp');
      const syncRequest = syncIndex.openCursor(
        IDBKeyRange.upperBound(cutoffTime)
      );

      syncRequest.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          // Only completed items age out. A failed item is unsent field
          // work; it stays until the inspector retries or clears it
          // (audit 2026-09-04, H6).
          if (cursor.value.status === 'completed') {
            cursor.delete();
          }
          cursor.continue();
        }
      };

      // Clear old synced inspections
      const inspectionStore = transaction.objectStore('inspections');
      const inspectionIndex = inspectionStore.index('lastModified');
      const inspectionRequest = inspectionIndex.openCursor(
        IDBKeyRange.upperBound(cutoffTime)
      );

      inspectionRequest.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          if (cursor.value.syncStatus === 'synced') {
            cursor.delete();
          }
          cursor.continue();
        }
      };

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }
}

export const offlineStorage = new OfflineStorage();
